import { Typography, Box, Container, Button, Grid2, Paper } from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SendIcon from '@mui/icons-material/Send';
import VideocamIcon from '@mui/icons-material/Videocam';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { EmployerLanding, HowItWorks1, HowItWorks5 } from '@carcareers/assets';
import { isMobile } from '@carcareers/utils';
import {
  CircularProgress,
  ExcelsusBanner,
  Intercom,
  TestimonialCarousel,
  RecruitingServicesButton,
} from '@carcareers/components';
import { useStats } from '@carcareers/hooks';

export const Employers: FC = () => {
  const navigate = useNavigate();

  // Track campaign visits when page loads with campaign parameter
  useStats();

  return (
    <>
      <Intercom />
      {/* Announcement Banner */}
      <Box
        sx={{
          bgcolor: '#231f20',
          py: 1,
          boxShadow: 3,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 2, md: 4 },
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NewReleasesIcon
                  sx={{
                    color: '#ce222f',
                    fontSize: { xs: 28, md: 36 },
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                  }}
                >
                  Background Checks for Less!
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="large"
              component="a"
              href="https://excelsusdata.com/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: '#ce222f',
                color: 'white',
                px: 2,
                py: 1,
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  bgcolor: '#a51b27',
                  color: 'white',
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Learn More
            </Button>
          </Box>
        </Container>
      </Box>

      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            color: 'white',
            pt: { xs: 8, md: 15 },
            pb: { xs: 30, md: 40 },
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              zIndex: 0,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              transform: 'scaleX(-1)',
              backgroundImage: `url(${EmployerLanding})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              width: '100%',
              height: '100px',
              zIndex: 1,
              background: 'linear-gradient(to bottom, rgba(255,255,255, 0.) 0%, rgba(255,255,255,1) 100%)',
            }}
          />
          <Container maxWidth="xl" sx={{ height: '500px', mt: { xs: 10, md: 20 } }}>
            <Box sx={{ maxWidth: { xs: '100%', md: '46%' } }}>
              <Typography
                variant="h1"
                component="h1"
                gutterBottom
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.75rem' },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: 'white',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                A Hiring Site for{' '}
                <Box
                  component="span"
                  sx={{
                    position: 'relative',
                    whiteSpace: 'nowrap',
                    zIndex: 1,
                  }}
                >
                  Auto-Related
                  <Box
                    sx={{
                      backgroundColor: 'warning.light',
                      borderRadius: '10px',
                      width: '100%',
                      height: '5px',
                      position: 'absolute',
                      bottom: 2,
                      left: 0,
                      zIndex: -1,
                      animation: 'wipeIn 2s cubic-bezier(0.8, 0, 0.2, 1)',
                      '@keyframes wipeIn': {
                        '0%': {
                          width: '0%',
                        },
                        '100%': {
                          width: '100%',
                        },
                      },
                    }}
                  />
                </Box>{' '}
                Careers!
              </Typography>
              <Typography
                component="h2"
                variant="body1"
                paragraph
                sx={(theme) => ({
                  mb: 8,
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                  color: theme.palette.grey[200],
                  position: 'relative',
                  zIndex: 1,
                })}
              >
                Hiring for anybody, everybody, anything, everything automotive related. From HR, sales, parts, drivers,
                techs, body shops to auto-related manufacturing.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  position: 'relative',
                  zIndex: 1,
                  '&:hover': {
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                  },
                }}
                onClick={() => navigate('/auth/signup/employer')}
              >
                Post my job
              </Button>
              <RecruitingServicesButton
                variant="contained"
                color="warning"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </Box>
          </Container>
        </Box>
      </Box>
      {/* Excelsus Data Banner */}
      <ExcelsusBanner />

      <Container maxWidth="lg" sx={{ py: { xs: 10, md: 16 } }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            height: '100%',
            textAlign: 'center',
            borderRadius: 3,
            backgroundColor: '#F5F7FA',
          }}
        >
          <Typography component="h2" variant="h2" sx={{ mb: 2 }}>
            Smarter Hiring Starts Here!
          </Typography>
          <Typography component="p" variant="body1" sx={{ mb: 5 }}>
            CARCAREERS.COM streamlines hiring so you meet the right people—fast.
          </Typography>
          <Grid2 container spacing={6}>
            <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <SendIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2, transform: 'rotate(-45deg)' }} />
                <Typography component="h3" variant="h5" gutterBottom>
                  Fast Job Postings
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Create your own, detailed job postings to send to 100+ job boards in minutes. Saving you time and
                  money.
                </Typography>
              </Paper>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <PeopleIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography component="h3" variant="h5" gutterBottom>
                  Quality Candidates
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Access a curated pool of experienced automotive professionals.
                </Typography>
              </Paper>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <CameraAltIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography component="h3" variant="h5" gutterBottom>
                  Video Resume
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Our video resume features will cut hiring time for you, the employer. You can see and hear interested
                  candidates before they come to you!
                </Typography>
              </Paper>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography component="h3" variant="h5" gutterBottom>
                  Recruiting Services
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Our recruiting service gets you to the right employee 5x as fast as any other service. <br />
                  (additional fees apply){' '}
                </Typography>
              </Paper>
            </Grid2>
          </Grid2>
        </Paper>
      </Container>
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography
          component="h3"
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 15,
          }}
        >
          Don't Just Take Our Word For It!
        </Typography>
        <TestimonialCarousel />
      </Container>
      <Box sx={{ bgcolor: 'primary.dark', py: { xs: 10, md: 16 }, mb: 10 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            component="h3"
            variant="h2"
            sx={{
              fontWeight: 700,
              color: 'primary.contrastText',
              mb: 3,
            }}
          >
            Ready to Fill Your Open Position?
          </Typography>
          <Typography variant="h3" color="primary.contrastText">
            Post your own job for
          </Typography>
          <Typography variant="h1" color="primary.contrastText" sx={{ my: 1 }}>
            $ 329
          </Typography>
          <Typography variant="h3" color="primary.contrastText">
            for 30 days.
          </Typography>
          <Button
            variant="contained"
            sx={{
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
              mt: 4,
            }}
            onClick={() => navigate('/auth/signup/employer')}
          >
            Get Started
          </Button>
        </Container>
      </Box>
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography
          component="h3"
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 15,
          }}
        >
          How It Works
        </Typography>
        <Timeline
          position={isMobile() ? 'right' : 'alternate'}
          sx={{
            maxWidth: 800,
            mx: 'auto',
            [`& .MuiTimelineItem-root:before`]: {
              flex: isMobile() ? 0 : 1,
            },
          }}
        >
          <TimelineItem sx={{ minHeight: isMobile() ? '150px' : '250px' }}>
            {!isMobile() && (
              <TimelineOppositeContent>
                <video
                  autoPlay
                  loop
                  muted
                  style={{
                    marginRight: '50px',
                    width: '150px',
                    height: '150px',
                  }}
                >
                  <source src={HowItWorks1} type="video/webm" />
                </video>
              </TimelineOppositeContent>
            )}
            <TimelineSeparator>
              <TimelineDot color="primary">
                <SendIcon sx={{ transform: 'rotate(-45deg) translate(1px, -1px)' }} />
              </TimelineDot>
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Box
                sx={{
                  opacity: 0,
                  transform: 'translateY(20px)',
                  animation: 'fadeIn 0.6s ease-out forwards',
                  '@keyframes fadeIn': {
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                <Typography variant="h4" gutterBottom>
                  Send your job posting out to candidates in minutes.
                </Typography>
                <Typography color="text.secondary">
                  Create detailed job postings to send to 100+ job boards. Saving you time and money.
                </Typography>
              </Box>
            </TimelineContent>
          </TimelineItem>
          <TimelineItem sx={{ minHeight: isMobile() ? '150px' : '250px' }}>
            <TimelineSeparator>
              <TimelineDot color="primary">
                <PeopleIcon />
              </TimelineDot>
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Box
                sx={{
                  opacity: 0,
                  transform: 'translateY(20px)',
                  animation: 'fadeIn 0.6s ease-out forwards',
                  animationDelay: '0.3s',
                }}
              >
                <Typography variant="h4" gutterBottom>
                  Get Quality Candidates
                </Typography>
                <Typography color="text.secondary">
                  Get qualified candidates that are a great fit for your job.
                </Typography>
              </Box>
            </TimelineContent>
          </TimelineItem>
          <TimelineItem sx={{ minHeight: isMobile() ? '150px' : '250px' }}>
            <TimelineSeparator>
              <TimelineDot color="primary">
                <VideocamIcon />
              </TimelineDot>
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Box
                sx={{
                  opacity: 0,
                  transform: 'translateY(20px)',
                  animation: 'fadeIn 0.6s ease-out forwards',
                  animationDelay: '0.6s',
                }}
              >
                <Typography variant="h4" gutterBottom>
                  Review Video resumes
                </Typography>
                <Typography color="text.secondary">
                  Cut hiring time to pre-screen interested candidates before they come to your store.
                </Typography>
              </Box>
            </TimelineContent>
          </TimelineItem>
          <TimelineItem sx={{ minHeight: isMobile() ? '150px' : '250px' }}>
            <TimelineSeparator>
              <TimelineDot color="primary">
                <ShowChartIcon />
              </TimelineDot>
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Box
                sx={{
                  opacity: 0,
                  transform: 'translateY(20px)',
                  animation: 'fadeIn 0.6s ease-out forwards',
                  animationDelay: '0.3s',
                }}
              >
                <Typography variant="h4" gutterBottom>
                  Rank Candidates for Better Results
                </Typography>
                <Typography color="text.secondary">
                  Our employer dashboard will help you sort, review, and rank candidates.
                </Typography>
              </Box>
            </TimelineContent>
          </TimelineItem>
          <TimelineItem sx={{ minHeight: isMobile() ? '150px' : '250px' }}>
            {!isMobile() && (
              <TimelineOppositeContent>
                <video
                  autoPlay
                  muted
                  style={{
                    marginRight: '50px',
                    marginTop: -50,
                    width: '150px',
                    height: '150px',
                  }}
                >
                  <source src={HowItWorks5} type="video/webm" />
                </video>
              </TimelineOppositeContent>
            )}
            <TimelineSeparator>
              <TimelineDot color="primary">
                <HowToRegIcon />
              </TimelineDot>
            </TimelineSeparator>
            <TimelineContent>
              <Box
                sx={{
                  opacity: 0,
                  transform: 'translateY(20px)',
                  animation: 'fadeIn 0.6s ease-out forwards',
                  animationDelay: '0.9s',
                }}
              >
                <Typography variant="h4" gutterBottom>
                  Hire Great Talent
                </Typography>
                <Typography color="text.secondary">
                  Connect with and hire the perfect candidates for your team.
                </Typography>
              </Box>
            </TimelineContent>
          </TimelineItem>
        </Timeline>
      </Container>
      <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 16 }, textAlign: 'center', width: '100%' }}>
        <Typography
          component="h3"
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 10,
          }}
        >
          Our Placement Success
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: { xs: 4, md: 16 },
            width: '100%',
          }}
        >
          <CircularProgress progress={94} progressLabel={96} label="Retention Rate" />
          <CircularProgress progress={97.5} progressLabel={99.6} label="Repeat & Referrals" />
        </Box>
      </Container>
      <Box sx={{ bgcolor: 'primary.dark', py: { xs: 10, md: 16 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            component="h3"
            variant="h2"
            sx={{
              fontWeight: 700,
              color: 'primary.contrastText',
              mb: 3,
            }}
          >
            Ready to Transform Your Hiring?
          </Typography>
          <Button
            variant="contained"
            sx={{
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
              mt: 4,
            }}
            onClick={() => navigate('/auth/signup/employer')}
          >
            Create a Free Account
          </Button>
        </Container>
      </Box>
    </>
  );
};
